import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  CreateRecurrenceInboxEntryInput,
  RecurrenceInboxEntry,
  UpdateRecurrenceInboxEntryInput,
} from '@this+that/models';

const RECURRENCE_INBOX_ENTRY_FIELDS = gql`
  fragment RecurrenceInboxEntryFields on RecurrenceInboxEntry {
    taskGroupId
    id
    createdFor
    subject
    description
    priority
    taskLabelIds
    cronExpression
    cronExpressionTimezone
    startDate
    endDate
    advanceNoticeInDays
    referenceInboxEntryId
    createdAt
    updatedAt
  }
`;

const GET_PAGINATED_RECURRENCE_INBOX_ENTRIES = gql`
  ${RECURRENCE_INBOX_ENTRY_FIELDS}
  query getPaginatedRecurrenceInboxEntries($limit: Int!, $exclusiveStartKey: String, $taskGroupId: String!) {
    getPaginatedRecurrenceInboxEntries(
      limit: $limit
      exclusiveStartKey: $exclusiveStartKey
      taskGroupId: $taskGroupId
    ) {
      recurrenceInboxEntries {
        ...RecurrenceInboxEntryFields
      }
      exclusiveStartKey
    }
  }
`;

interface GetPaginatedRecurrenceInboxEntriesParams {
  limit: number;
  exclusiveStartKey?: string | null;
  taskGroupId?: string;
}

interface GetPaginatedRecurrenceInboxEntriesData {
  getPaginatedRecurrenceInboxEntries: {
    recurrenceInboxEntries: RecurrenceInboxEntry[];
    exclusiveStartKey: string | null;
  };
}

export function useGetPaginatedRecurrenceInboxEntries() {
  const { loading, refetch } = useQuery<GetPaginatedRecurrenceInboxEntriesData>(
    GET_PAGINATED_RECURRENCE_INBOX_ENTRIES,
    { skip: true },
  );

  const getPaginatedRecurrenceInboxEntries = useCallback(
    async ({
      limit,
      exclusiveStartKey,
      taskGroupId,
    }: GetPaginatedRecurrenceInboxEntriesParams): Promise<{
      recurrenceInboxEntries: RecurrenceInboxEntry[];
      exclusiveStartKey: string | null;
    }> => {
      const { data } = await refetch({ limit, exclusiveStartKey, taskGroupId });

      return (
        data?.getPaginatedRecurrenceInboxEntries ?? {
          recurrenceInboxEntries: [],
          exclusiveStartKey: null,
        }
      );
    },
    [refetch],
  );

  return { getPaginatedRecurrenceInboxEntries, loading };
}

const CREATE_RECURRENCE_INBOX_ENTRY = gql`
  ${RECURRENCE_INBOX_ENTRY_FIELDS}
  mutation createRecurrenceInboxEntry($recurrenceInboxEntry: CreateRecurrenceInboxEntryInput!) {
    createRecurrenceInboxEntry(recurrenceInboxEntry: $recurrenceInboxEntry) {
      ...RecurrenceInboxEntryFields
    }
  }
`;

interface CreateRecurrenceInboxEntryData {
  createRecurrenceInboxEntry: RecurrenceInboxEntry;
}

export function useCreateRecurrenceInboxEntry() {
  const [createRecurrenceInboxEntryMutation, { loading, error }] =
    useMutation<CreateRecurrenceInboxEntryData>(CREATE_RECURRENCE_INBOX_ENTRY);

  const createRecurrenceInboxEntry = useCallback(
    async (recurrenceInboxEntry: CreateRecurrenceInboxEntryInput) => {
      const { data } = await createRecurrenceInboxEntryMutation({
        variables: { recurrenceInboxEntry },
      });
      return data?.createRecurrenceInboxEntry;
    },
    [createRecurrenceInboxEntryMutation],
  );

  return { createRecurrenceInboxEntry, loading, error };
}

const UPDATE_RECURRENCE_INBOX_ENTRY = gql`
  ${RECURRENCE_INBOX_ENTRY_FIELDS}
  mutation updateRecurrenceInboxEntry($recurrenceInboxEntry: UpdateRecurrenceInboxEntryInput!) {
    updateRecurrenceInboxEntry(recurrenceInboxEntry: $recurrenceInboxEntry) {
      ...RecurrenceInboxEntryFields
    }
  }
`;

interface UpdateRecurrenceInboxEntryData {
  updateRecurrenceInboxEntry: RecurrenceInboxEntry;
}

export function useUpdateRecurrenceInboxEntry() {
  const [updateRecurrenceInboxEntryMutation, { loading, error }] =
    useMutation<UpdateRecurrenceInboxEntryData>(UPDATE_RECURRENCE_INBOX_ENTRY);

  const updateRecurrenceInboxEntry = useCallback(
    async (recurrenceInboxEntry: UpdateRecurrenceInboxEntryInput) => {
      const { data } = await updateRecurrenceInboxEntryMutation({
        variables: { recurrenceInboxEntry },
      });
      return data?.updateRecurrenceInboxEntry;
    },
    [updateRecurrenceInboxEntryMutation],
  );

  return { updateRecurrenceInboxEntry, loading, error };
}

const GET_RECURRENCE_INBOX_ENTRY_BY_REFERENCE_ID = gql`
  ${RECURRENCE_INBOX_ENTRY_FIELDS}
  query getRecurrenceInboxEntryByReferenceId($referenceInboxEntryId: String!) {
    getRecurrenceInboxEntryByReferenceId(referenceInboxEntryId: $referenceInboxEntryId) {
      ...RecurrenceInboxEntryFields
    }
  }
`;

interface GetRecurrenceInboxEntryByReferenceIdData {
  getRecurrenceInboxEntryByReferenceId: RecurrenceInboxEntry | null;
}

export function useGetRecurrenceInboxEntryByReferenceId() {
  const { loading, refetch } =
    useQuery<GetRecurrenceInboxEntryByReferenceIdData>(
      GET_RECURRENCE_INBOX_ENTRY_BY_REFERENCE_ID,
      { skip: true },
    );

  const getRecurrenceInboxEntryByReferenceId = useCallback(
    async (
      referenceInboxEntryId: string,
    ): Promise<RecurrenceInboxEntry | null> => {
      const { data } = await refetch({ referenceInboxEntryId });
      return data?.getRecurrenceInboxEntryByReferenceId ?? null;
    },
    [refetch],
  );

  return { getRecurrenceInboxEntryByReferenceId, loading };
}

const DELETE_RECURRENCE_INBOX_ENTRY = gql`
  mutation deleteRecurrenceInboxEntry($taskGroupId: String!, $id: String!) {
    deleteRecurrenceInboxEntry(taskGroupId: $taskGroupId, id: $id)
  }
`;

interface DeleteRecurrenceInboxEntryData {
  deleteRecurrenceInboxEntry: boolean;
}

export function useDeleteRecurrenceInboxEntry() {
  const [deleteRecurrenceInboxEntryMutation, { loading, error }] =
    useMutation<DeleteRecurrenceInboxEntryData>(DELETE_RECURRENCE_INBOX_ENTRY);

  const deleteRecurrenceInboxEntry = useCallback(
    async (taskGroupId: string, id: string) => {
      const { data } = await deleteRecurrenceInboxEntryMutation({
        variables: { taskGroupId, id },
      });
      return data?.deleteRecurrenceInboxEntry ?? false;
    },
    [deleteRecurrenceInboxEntryMutation],
  );

  return { deleteRecurrenceInboxEntry, loading, error };
}

const MOVE_RECURRENCE_INBOX_ENTRY_TO_TASK_GROUP = gql`
  ${RECURRENCE_INBOX_ENTRY_FIELDS}
  mutation moveRecurrenceInboxEntryToTaskGroup($taskGroupId: String!, $id: String!, $newTaskGroupId: String!) {
    moveRecurrenceInboxEntryToTaskGroup(taskGroupId: $taskGroupId, id: $id, newTaskGroupId: $newTaskGroupId) {
      ...RecurrenceInboxEntryFields
    }
  }
`;

interface MoveRecurrenceInboxEntryToTaskGroupData {
  moveRecurrenceInboxEntryToTaskGroup: RecurrenceInboxEntry;
}

export function useMoveRecurrenceInboxEntryToTaskGroupService() {
  const [moveRecurrenceInboxEntryMutation, { loading, error }] =
    useMutation<MoveRecurrenceInboxEntryToTaskGroupData>(
      MOVE_RECURRENCE_INBOX_ENTRY_TO_TASK_GROUP,
    );

  const moveRecurrenceInboxEntryToTaskGroup = useCallback(
    async (taskGroupId: string, id: string, newTaskGroupId: string) => {
      const { data } = await moveRecurrenceInboxEntryMutation({
        variables: { taskGroupId, id, newTaskGroupId },
      });
      return data?.moveRecurrenceInboxEntryToTaskGroup;
    },
    [moveRecurrenceInboxEntryMutation],
  );

  return { moveRecurrenceInboxEntryToTaskGroup, loading, error };
}
